import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useSnapshot } from 'valtio'
import { GetFavouriteListForProfileDocument } from '@app/graphql'
import { IconTypes } from '@app/ui'
import { FilterChannelState, DateRange, updateChannelFilters } from './state'
import { Sport } from './types'
import { SPORT_ICON_MAP } from './constants'

export const useFilterState = () => {
  const snap = useSnapshot(FilterChannelState)

  const [ selectedTipType, setSelectedTipType ] = useState(snap.filters.tipType)
  const [ selectedAnalysis, setSelectedAnalysis ] = useState(snap.filters.analysis)
  const [ selectedDate, setSelectedDate ] = useState<DateRange>(snap.filters.date)
  const [ selectedSports, setSelectedSports ] = useState<string[]>(snap.filters.sports as string[])
  const [ showAllSports, setShowAllSports ] = useState(false)

  useEffect(() => {
    setSelectedTipType(snap.filters.tipType)
    setSelectedAnalysis(snap.filters.analysis)
    setSelectedDate(snap.filters.date)
    setSelectedSports(snap.filters.sports as string[])
  }, [ snap.filters ])

  const onSelectTipType = (value: any) => {
    setSelectedTipType(value)
    updateChannelFilters({ tipType: value })
  }

  const onSelectAnalysis = (value: any) => {
    setSelectedAnalysis(value)
    updateChannelFilters({ analysis: value })
  }

  const onSelectDate = (value: DateRange) => {
    setSelectedDate(value)
    updateChannelFilters({ date: value })
  }

  const onSelectSports = (value: string[]) => {
    setSelectedSports(value)
    updateChannelFilters({ sports: value })
  }

  return {
    selectedTipType,
    setSelectedTipType,
    selectedAnalysis,
    setSelectedAnalysis,
    selectedDate,
    setSelectedDate: onSelectDate,
    selectedSports,
    setSelectedSports: onSelectSports,
    showAllSports,
    setShowAllSports,
    onSelectTipType,
    onSelectAnalysis
  }
}
export const useSportsData = () => {
  const {
    data: sportsNmarketData,
    loading: sportsNmarketLoading,
    error: sportsNmarketError,
  } = useQuery(GetFavouriteListForProfileDocument, { fetchPolicy: 'cache-first' })

  const sportsOptions = useMemo((): Sport[] => {
    if (!sportsNmarketData?.sports) return []

    return sportsNmarketData.sports.map(sport => ({
      id: sport.id,
      name: sport.name,
      icon: SPORT_ICON_MAP[sport.name] || IconTypes.tennis
    }))
  }, [ sportsNmarketData ])

  return {
    sportsOptions,
    sportsNmarketLoading,
    sportsNmarketError
  }
}

export const useSportToggle = (selectedSports: string[], setSelectedSports: (sports: string[]) => void) => {
  const handleSportToggle = (sportId: string) => {
    setSelectedSports(
      selectedSports.includes(sportId)
        ? selectedSports.filter(id => id !== sportId)
        : [ ...selectedSports, sportId ]
    )
  }

  return { handleSportToggle }
}

export const useActiveFilterCount = () => {
  const snap = useSnapshot(FilterChannelState)

  return useMemo(() => {
    let count = 0
    if (snap.filters.tipType.id !== 1) {
      count++
    }

    if (snap.filters.analysis.id !== 1) {
      count++
    }

    if (snap.filters.date.label !== 'All') {
      count++
    }

    if (snap.filters.sports.length > 0) {
      count++
    }

    return count
  }, [ snap.filters ])
}
